import { phrase } from './i18n/runtime';

const formatterCache = new Map<string, Intl.DateTimeFormat>();

export type FormatLocale = 'EN' | 'HY' | 'RU';

let formatLocale: FormatLocale = 'EN';

/** Dates follow the active interface language. Tests leave this at English. */
export function setFormatLocale(locale: FormatLocale): void {
  if (formatLocale === locale) return;
  formatLocale = locale;
  formatterCache.clear();
}

export function getFormatLocale(): FormatLocale {
  return formatLocale;
}

function intlLocale(): string {
  if (formatLocale === 'HY') return 'hy-AM';
  if (formatLocale === 'RU') return 'ru-RU';
  return 'en-GB';
}

function formatter(
  key: string,
  options: Intl.DateTimeFormatOptions,
  locale = intlLocale(),
): Intl.DateTimeFormat {
  const cacheKey = `${locale}:${key}`;
  let cached = formatterCache.get(cacheKey);
  if (cached === undefined) {
    cached = new Intl.DateTimeFormat(locale, options);
    formatterCache.set(cacheKey, cached);
  }
  return cached;
}

const EN_SHORT_MONTHS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
];
const HY_SHORT_MONTHS = [
  'հնվ',
  'փտվ',
  'մրտ',
  'ապր',
  'մյս',
  'հնս',
  'հլս',
  'օգս',
  'սեպ',
  'հոկ',
  'նոյ',
  'դեկ',
];

/** Desktop browsers often ship without Armenian date data and fall back to the system language. */
function formatDisplay(key: string, options: Intl.DateTimeFormatOptions, date: Date): string {
  if (formatLocale !== 'HY') {
    return formatter(key, options).format(date);
  }
  return formatter(key, options, 'en-GB')
    .formatToParts(date)
    .map((part) => {
      if (part.type !== 'month') return part.value;
      const index = EN_SHORT_MONTHS.indexOf(part.value.slice(0, 3).toLowerCase());
      return HY_SHORT_MONTHS[index] ?? part.value;
    })
    .join('');
}

function safeTimeZone(timeZone: string | undefined): string | undefined {
  if (timeZone === undefined || timeZone === '') {
    return undefined;
  }
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone });
    return timeZone;
  } catch {
    return undefined;
  }
}

/** Instant shown unambiguously in the given IANA zone, e.g. "10 Oct 2026, 21:20 GMT+4". */
export function formatDateTime(iso: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  const zone = safeTimeZone(timeZone);
  return formatDisplay(
    `dt:${zone ?? ''}`,
    {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      timeZoneName: 'short',
      ...(zone !== undefined ? { timeZone: zone } : {}),
    },
    date,
  );
}

/** Calendar date (`YYYY-MM-DD`, no zone) shown as "10 Oct 2026" without shifting the day. */
export function formatDate(isoDate: string): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) {
    return isoDate;
  }
  return formatDisplay(
    'd',
    { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' },
    date,
  );
}

/** Today's calendar date in an IANA zone as `YYYY-MM-DD`. */
export function todayIn(timeZone?: string): string {
  const zone = safeTimeZone(timeZone);
  const parts = formatter(`today:${zone ?? ''}`, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(zone !== undefined ? { timeZone: zone } : {}),
  }).formatToParts(new Date());
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/** Percentage string from the API ("33.333300") without insignificant zeros ("33.3333 %"). */
export function formatPercent(value: string): string {
  const [whole = value, fraction] = value.split('.');
  const trimmed = fraction?.replace(/0+$/, '') ?? '';
  return `${trimmed === '' ? whole : `${whole}.${trimmed}`} %`;
}

/** Share counts are integer strings of arbitrary size; group digits without using floats. */
export function formatShareCount(value: string): string {
  return /^\d+$/.test(value) ? value.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : value;
}

/** "LEGAL_ADMIN" → "Legal admin". */
export function humanize(value: string): string {
  const text = value.replace(/_/g, ' ').toLowerCase();
  return phrase(text.charAt(0).toUpperCase() + text.slice(1));
}
